'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { EventIdentity, type EventIdentityAsset } from '@/components/live/event-identity';
import type { LiveEventSummary } from '@/components/live/types';
import { Icon } from '@/components/ui/icon';
import { ArrowRight01Icon, Timer01Icon } from '@/components/ui/icon/glyphs';
import { NavLink } from '@/components/ui/navigation-list';
import { Panel } from '@/components/ui/panel';
export interface ResolvedEventAssociationProps {
  event: LiveEventSummary;
  eventLogo: EventIdentityAsset | null;
  participantLogos?: readonly EventIdentityAsset[] | null | undefined;
  timeZone: string;
}
function formatEventWindow(event: LiveEventSummary, locale: string, timeZone: string): string {
  const date = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone
  });
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone
  });
  const start = new Date(event.start);
  const end = new Date(event.end);
  const startDay = date.format(start);
  const endDay = date.format(end);
  if (startDay === endDay) return `${startDay} · ${time.format(start)} – ${time.format(end)}`;
  return `${startDay}, ${time.format(start)} – ${endDay}, ${time.format(end)}`;
}
export function ResolvedEventAssociation(t0) {
  const $ = _c(32);
  const {
    event,
    eventLogo,
    participantLogos,
    timeZone
  } = t0;
  const locale = useLocale();
  const t = useTranslations("liveEvents.resolvedEventAssociation");
  let t1;
  if ($[0] !== t) {
    t1 = t("aria");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={Timer01Icon} className="size-3.5 text-foreground-tertiary" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("title");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="flex h-10 items-center gap-1.75 border-b border-border-secondary px-4 text-caption font-medium text-foreground-secondary">{t2}<span>{t3}</span></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `/live/${encodeURIComponent(event.id)}`;
  let t6;
  if ($[7] !== eventLogo || $[8] !== participantLogos) {
    t6 = <EventIdentity eventLogo={eventLogo} participantLogos={participantLogos} size="event-time-card" />;
    $[7] = eventLogo;
    $[8] = participantLogos;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== event.title) {
    t7 = <strong className="whitespace-normal text-body font-medium text-foreground-primary">{event.title}</strong>;
    $[10] = event.title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== event || $[13] !== locale || $[14] !== timeZone) {
    t8 = formatEventWindow(event, locale, timeZone);
    $[12] = event;
    $[13] = locale;
    $[14] = timeZone;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <span className="text-caption text-foreground-tertiary">{t8}</span>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <span className="flex min-w-0 flex-col gap-0.5">{t7}{t9}</span>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <span className="flex min-w-0 flex-1 items-center gap-2 @min-[480px]/event-time:gap-3">{t6}{t10}</span>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon icon={ArrowRight01Icon} aria-hidden={true} className="size-4 shrink-0 text-foreground-tertiary transition-transform group-hover:translate-x-0.5 group-hover:text-foreground-primary" />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t5) {
    t13 = <NavLink href={t5} prefetch={false} className="group flex min-w-0 items-center justify-between gap-3 px-3 py-3.5 hover:bg-surface-secondary @min-[480px]/event-time:px-4">{t11}{t12}</NavLink>;
    $[25] = t11;
    $[26] = t5;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t1 || $[29] !== t13 || $[30] !== t4) {
    t14 = <Panel data-testid="resolved-event-association" role="group" aria-label={t1} density="flush" className="@container/event-time overflow-hidden">{t4}{t13}</Panel>;
    $[28] = t1;
    $[29] = t13;
    $[30] = t4;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}